/**
 * MIDAS V3 — Image resize helper for composer attach upload.
 *
 * Behaviour (copied verbatim from V2 `ChatInputAreaV2.tsx` lines 68-125):
 *   - Max dimension 2048px (longest side); smaller images pass through untouched.
 *   - PNG preserved as PNG, everything else encoded as JPEG quality 0.85.
 *   - Output File keeps the original stem and gets `.png` / `.jpg` extension.
 *
 * **Duplicate notice:** V2 still inlines the same function inside
 * `customer/midas/components/v2/ChatInputAreaV2.tsx`. V2 is byte-frozen for the
 * V2/V3 split, so we intentionally do NOT update it to import from here. A
 * future cleanup task can swap V2 over to this shared util and delete the
 * V2-inline copy.
 *
 * Constants in this file mirror V2 1:1. If V2 ever changes, update both.
 */

export const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
export const MAX_IMAGE_DIMENSION = 2048;
export const JPEG_QUALITY = 0.85;
export const ACCEPTED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
];
export const ACCEPTED_IMAGE_EXTENSIONS = ".jpeg,.jpg,.png,.webp,.heic";

export const resizeImageForUpload = (file: File): Promise<File> => {
  return new Promise((resolve) => {
    const isPng = file.type === "image/png";
    const outputMime = isPng ? "image/png" : "image/jpeg";
    const outputExt = isPng ? ".png" : ".jpg";

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const { naturalWidth: w, naturalHeight: h } = img;

      if (w <= MAX_IMAGE_DIMENSION && h <= MAX_IMAGE_DIMENSION) {
        resolve(file);
        return;
      }

      const scale = Math.min(MAX_IMAGE_DIMENSION / w, MAX_IMAGE_DIMENSION / h);
      const newW = Math.round(w * scale);
      const newH = Math.round(h * scale);

      const canvas = document.createElement("canvas");
      canvas.width = newW;
      canvas.height = newH;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(file);
        return;
      }
      ctx.drawImage(img, 0, 0, newW, newH);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(file);
            return;
          }
          const stem = file.name.replace(/\.[^.]+$/, "");
          const resizedFile = new File([blob], `${stem}${outputExt}`, {
            type: outputMime,
            lastModified: Date.now(),
          });
          resolve(resizedFile);
        },
        outputMime,
        isPng ? undefined : JPEG_QUALITY
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file);
    };

    img.src = objectUrl;
  });
};
